// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  BillingPlanFilter,
  getBillingPlans,
  serializeBillingPlanFilter,
} from '@/billing/api/get-billing-plans';
import { TWO_MINUTES_MS } from '@/utilities/time-constants';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

interface UseBillingPlansOptions {
  filter?: BillingPlanFilter;
}

export function useBillingPlans(options: UseBillingPlansOptions = {}) {
  const filter = options.filter ?? { pageSize: 20 };
  const serializedFilter = serializeBillingPlanFilter(filter);

  const { data, isLoading, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['billing', 'plans', serializedFilter],
    queryFn: () => getBillingPlans(filter),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchInterval: TWO_MINUTES_MS,
  });

  return {
    plans: data?.results ?? null,
    totalCount: data?.totalCount ?? null,
    statusSummary: data?.statusSummary ?? null,
    amountRange: data?.amountRange ?? null,
    isLoading,
    isFetching,
    isPlaceholderData,
  };
}
